import { useState } from "react";

import Box from "@mui/material/Box";
import { Tab, Tabs } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import PlantHoursDG from "./PlantHoursDG";
import PtoReview from "../pto_dash/PtoReview";
import HolidayPayReview from "@components/holiday/HolidayPayReview";
import Spinner from "@components/Spinner";
import useContractorsData from "../../hooks/useContractorsData";
import useHolidayPayments from "../../hooks/useHolidayPayments";

const AlternativeTable = () => {
  const [periodStart, setPeriodStart] = useState(getPreviousSunday()); // Initialize periodStart with a UTC date string
  const [activeTab, setActiveTab] = useState(0);

  // custom hook to fetch data
  const { plants, isLoading, error } = useContractorsData(periodStart);
  const { holidayPayments } = useHolidayPayments(periodStart);

  function getPreviousSunday() {
    const today = new Date();
    const dayOfWeek = today.getDay(); // Sunday - 0, Monday - 1, etc.
    const daysSinceLastSunday = dayOfWeek + 7; // Because Sunday is 0
    const lastSunday = new Date(
      today.setDate(today.getDate() - daysSinceLastSunday)
    );
    lastSunday.setHours(0, 0, 0, 0); // Reset time to midnight
    return lastSunday;
  }

  const allWorkers = () => {
    let workers = [];
    plants.forEach((plant) => {
      workers = workers.concat(plant.workers);
    });
    return workers;
  };

  const allPtoRequests = () => {
    let ptoRequests = [];
    plants.forEach((plant) => {
      plant.workers.forEach((worker) => {
        ptoRequests = ptoRequests.concat(worker.pto_requests);
      });
    });
    return ptoRequests;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box
      sx={{
        // height: 500,
        width: {
          xs: "100%", // 100% width on extra-small devices
          sm: "100%", //'100%' width on small devices and up
          md: "68rem", // '100%' width on medium devices and up
          // lg: '68rem', //'80%' width on large devices and up
        },
        "& .actions": {
          color: "text.secondary",
        },
        "& .textPrimary": {
          color: "text.primary",
        },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      {error && (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            color: "error.main",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div>{error}</div>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          // justifyContent: 'end',
          alignItems: "start",
          margin: "1rem",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={new Intl.DateTimeFormat("en").format(getPreviousSunday())}
            slotProps={{
              textField: {
                helperText: "Select Pay Period Start Date (Sunday)",
              },
            }}
            onChange={(newValue) => {
              // Convert from dayjs to JS Date if needed
              const newDate =
                newValue && newValue.$d ? new Date(newValue.$d) : newValue;

              // Set to beginning of day in local time
              if (newDate) {
                newDate.setHours(0, 0, 0, 0);
              }

              setPeriodStart(newDate);
            }}
          />
        </LocalizationProvider>
      </Box>
      {isLoading && (
        <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-min-w-[580px] tw-min-h-[55vh]">
          <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center tw-w-full" />
          Loading data...
        </div>
      )}
      {!isLoading && !error && (
        <Box sx={{ width: "100%" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="timesheet and PTO review tabs"
            sx={{
              "& .MuiTabs-indicator": {
                display: "none",
              },
              minHeight: "20px",
            }}
          >
            <Tab
              label="Timesheets Review"
              sx={{
                backgroundColor:
                  activeTab === 0 ? "#2642c9 !important" : "white !important",
                color: activeTab === 0 ? "#fff !important" : "#000 !important",
                borderRadius: "6px 0px 0px 0px",
                minHeight: "30px !important",
                fontSize: "0.875rem",
                padding: "4px 12px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor:
                    activeTab === 0
                      ? "#2642c9 !important"
                      : "#d0d0d0 !important",
                },
              }}
            />
            <Tab
              label="PTO Review"
              sx={{
                backgroundColor:
                  activeTab === 1 ? "#2642c9 !important" : "white !important",
                color: activeTab === 1 ? "#fff !important" : "#000 !important",
                minHeight: "24px !important",
                fontSize: "0.875rem",
                padding: "9px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor:
                    activeTab === 1
                      ? "#2642c9 !important"
                      : "#d0d0d0 !important",
                },
              }}
            />
            <Tab
              label="Holiday Pay Review"
              sx={{
                backgroundColor:
                  activeTab === 2 ? "#2642c9 !important" : "white !important",
                color: activeTab === 2 ? "#fff !important" : "#000 !important",
                borderRadius: "0px 6px 0px 0px",
                minHeight: "24px !important",
                fontSize: "0.875rem",
                padding: "4px 12px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor:
                    activeTab === 2
                      ? "#2642c9 !important"
                      : "#d0d0d0 !important",
                },
              }}
            />
          </Tabs>
          <Box
            role="tabpanel"
            hidden={activeTab !== 0}
            id={`simple-tabpanel-${0}`}
            aria-labelledby={`simple-tab-${0}`}
            className="timesheets-container"
          >
            <Box>
              <div className="tw-rounded-r-lg tw-border tw-border-gray-200">
                {/* <h1 className="tw-text-xl tw-font-semibold tw-mb-4">
                  Timesheets Review
                </h1> */}
                {/* Timesheets content goes here */}
                {plants
                  .filter((plant) =>
                    plant.workers.some((worker) => worker.timecards.length > 0)
                  )
                  .map((plant) => (
                    <div className=" tw-bg-white tw-p-4 tw-mb-5" key={plant.id}>
                      <div className="tw-flex tw-justify-start tw-mb-2">
                        <h2 className="tw-rounded tw-p-1 tw-bg-[#2642c9] tw-text-white ">
                          {plant.name}
                        </h2>
                      </div>
                      <PlantHoursDG plant={plant} periodStart={periodStart} />
                    </div>
                  ))}
              </div>
            </Box>
          </Box>
          <Box
            role="tabpanel"
            hidden={activeTab !== 1}
            id={`simple-tabpanel-${1}`}
            aria-labelledby={`simple-tab-${1}`}
            className="pto-container"
          >
            <Box>
              <PtoReview
                allWorkers={allWorkers()}
                ptoRequests={allPtoRequests()}
              />
            </Box>
          </Box>
          <Box
            role="tabpanel"
            hidden={activeTab !== 2}
            id={`simple-tabpanel-${2}`}
            aria-labelledby={`simple-tab-${2}`}
            className="holiday-pay-container"
          >
            <Box>
              <HolidayPayReview holidayPayments={holidayPayments} />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AlternativeTable;
